import { useEffect, useState, useMemo, useCallback } from "react";
import adminAxios from "./adminAxios"; 
import AdminSidebar from "./AdminSidebar";
import { 
  ExternalLink, 
  Clipboard, 
  AlertCircle, 
  BarChart3, 
  ShieldCheck, 
  Search, 
  ArrowLeft, 
  ArrowRight, 
  X,
  History,
  Lock
} from "lucide-react";

const PAGE_SIZE = 10;

const THEME = {
  primary: "#0f172a",
  accent: "#38bdf8",
  success: "#10b981",
  danger: "#f43f5e",
  warning: "#f59e0b",
  bg: "#020617",
  surface: "#1e293b",
  border: "rgba(255, 255, 255, 0.08)",
  textMain: "#f8fafc",
  textMuted: "#94a3b8"
};

export default function WithdrawRequests() {
  const [withdraws, setWithdraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [actionId, setActionId] = useState(null);
  const [message, setMessage] = useState(null);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [adminTxId, setAdminTxId] = useState(""); 

  // Retrieve isolated tenant context identifier
  const tenantId = localStorage.getItem("adminId") || "global";

  /* ================= REGISTRY SYNC (GOLDEN PATH) ================= */
  const fetchWithdraws = useCallback(async () => {
    try {
      const { data } = await adminAxios.get(`/transactions/admin/withdrawals?t=${Date.now()}`, { params: { tenantId } });
      setWithdraws(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[System] Registry Fetch Failure", err);
      setWithdraws([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchWithdraws();
    const interval = setInterval(fetchWithdraws, 15000); 
    return () => clearInterval(interval);
  }, [fetchWithdraws]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  /* ================= RECONCILIATION LOGIC ================= */
  const handleUpdateStatus = async (id, type) => {
    const isApprove = type === "approve";
    
    if (isApprove && !adminTxId.trim()) {
      alert("CRITICAL: A Transaction Reference/Hash is required for settlement authorization.");
      return;
    }
    
    const confirmMsg = isApprove 
      ? "Authorize Liquidation? This action will adjust the node's balance permanently." 
      : "Issue Rejection: Decline this settlement request?";
    
    if (!window.confirm(confirmMsg)) return;

    try {
      setActionId(id);
      const targetId = String(id).trim();
      const endpoint = isApprove 
        ? `transactions/admin/withdraw/approve/${targetId}` 
        : `transactions/admin/withdraw/reject/${targetId}`;
        
      const { data } = await adminAxios.put(endpoint, { clearanceReference: adminTxId, tenantId });
      
      await fetchWithdraws();
      setMessage({ type: "success", text: data.msg || "Registry Updated Successfully" });
      setSelected(null);
      setAdminTxId("");
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || "Operation Protocol Failure" });
    } finally {
      setActionId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: "success", text: "Identifier copied to secure clipboard." });
  };

  /* ================= DATA PROCESSING ================= */
  const filteredWithdraws = useMemo(() => {
    return withdraws.filter((w) => {
      let statusMatch = false;
      if (filter === "all") statusMatch = true;
      else if (filter === "Awaiting Action") {
        statusMatch = ["Pending", "Processing", "Pending Verification"].includes(w.status);
      } else {
        statusMatch = w.status === filter;
      }

      const s = search.toLowerCase();
      const userName = w.userId?.name || "";
      const userEmail = w.userId?.email || "";
      const method = w.method || "";
      
      return statusMatch && (
        userName.toLowerCase().includes(s) || 
        userEmail.toLowerCase().includes(s) || 
        method.toLowerCase().includes(s)
      );
    });
  }, [withdraws, filter, search]);

  const stats = useMemo(() => {
    const total = withdraws.length;
    const processing = withdraws.filter((w) => ["Processing", "Pending", "Pending Verification"].includes(w.status));
    const completed = withdraws.filter((w) => ["Completed", "Settlement Finalized"].includes(w.status));

    return {
      total,
      processingCount: processing.length,
      completedAmount: completed.reduce((s, w) => s + Number(w.amount || 0), 0),
      processingAmount: processing.reduce((s, w) => s + Number(w.amount || 0), 0),
    };
  }, [withdraws]);

  const totalPages = Math.ceil(filteredWithdraws.length / PAGE_SIZE) || 1;
  const paginatedWithdraws = filteredWithdraws.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [filter, search]);

  if (loading) {
    return (
      <div style={styles.container} data-tenant-scope={tenantId}>
        <AdminSidebar />
        <div style={styles.main}>
            <div style={styles.loadingBox}>
              <div className="spin" style={styles.spinner}><ShieldCheck size={40} color={THEME.accent} /></div>
              <p>SYNCHRONIZING SECURE LEDGER...</p>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} data-tenant-scope={tenantId}>
      <AdminSidebar />

      <div style={styles.main}>
        {/* HEADER */}
        <div style={styles.topBar}>
          <div>
            <h1 style={styles.title}>SETTLEMENT REGISTRY</h1>
            <p style={styles.subTitle}>VERIFY AND AUTHORIZE RESOURCE LIQUIDATIONS</p>
          </div>
          <div style={styles.liveIndicator}>
            <div style={styles.pulseDot}></div>
            <span style={styles.liveText}>NODE TELEMETRY ACTIVE</span>
          </div>
        </div>

        <div style={styles.scrollArea}>
          <div style={styles.content}>
            {message && (
              <div style={styles.toast(message.type)}>
                {message.type === 'error' ? <AlertCircle size={18} /> : <ShieldCheck size={18} />}
                {message.text}
              </div>
            )}

            <div style={styles.statsGrid}>
              <StatCard icon={<History size={20}/>} label="Active Tickets" value={stats.total} accent={THEME.accent} />
              <StatCard icon={<AlertCircle size={20}/>} label="Pending Review" value={stats.processingCount} accent={THEME.warning} />
              <StatCard icon={<BarChart3 size={20}/>} label="Total Liquidated" value={`$${stats.completedAmount.toLocaleString()}`} accent={THEME.success} />
              <StatCard icon={<ExternalLink size={20}/>} label="Volume In Transit" value={`$${stats.processingAmount.toLocaleString()}`} accent={THEME.danger} />
            </div>

            <div style={styles.filterBar}>
              <div style={styles.btnGroup}>
                {["all", "Awaiting Action", "Settlement Finalized", "Request Declined"].map((s) => (
                  <button key={s} onClick={() => setFilter(s)} style={styles.filterBtn(filter === s)}>
                    {s === "all" ? "FULL REGISTRY" : s.toUpperCase()}
                  </button>
                ))}
              </div>
              <div style={styles.searchBox}>
                <Search size={18} color={THEME.textMuted} />
                <input
                  placeholder="FILTER NODE ID OR EMAIL..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={styles.searchInput}
                />
              </div>
            </div>

            <div style={styles.tableCard}>
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>IDENTITY NODE</th>
                      <th style={styles.th}>ALLOCATION</th>
                      <th style={styles.th}>ROUTING PROTOCOL</th>
                      <th style={styles.th}>VERIFICATION STATUS</th>
                      <th style={styles.th}>TIMESTAMP</th>
                      <th style={styles.th}>EXECUTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedWithdraws.map((w) => (
                      <tr key={String(w._id)} style={styles.tr}>
                        <td style={styles.td}>
                          <div style={styles.userName}>{w.userId?.name || "ID UNKNOWN"}</div>
                          <div style={styles.userEmail}>{w.userId?.email}</div>
                          <div style={styles.balanceTag}>CREDIT: ${Number(w.userId?.creditBalance || 0).toFixed(2)}</div>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.amount}>${Number(w.amount).toFixed(2)}</span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.methodName}>{w.method}</div>
                          <div style={styles.subText}>{w.institutionName || w.ledgerType || "Direct Entry"}</div>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.statusBadge(w.status)}>{w.status}</span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.timestamp}>
                            {new Date(w.createdAt).toLocaleDateString()}<br/>
                            {new Date(w.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <button onClick={() => { setSelected(w); setAdminTxId(w.clearanceReference || ""); }} style={styles.processBtn}>
                            PROCESS LOGIC
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {paginatedWithdraws.length === 0 && (
                  <div style={styles.empty}>NO RECORDS FOUND IN SELECTED INDEX.</div>
                )}
              </div>

              <div style={styles.pagination}>
                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} style={styles.pBtn}>
                  <ArrowLeft size={16} /> BACK
                </button>
                <div style={styles.pageInfo}>INDEX <span style={{color: THEME.accent}}>{page}</span> OF {totalPages}</div>
                <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} style={styles.pBtn}>
                  FORWARD <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {selected && (
          <div style={styles.modalOverlay} onClick={() => setSelected(null)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}><Lock size={18} style={{marginRight: 10}}/> NODE RECONCILIATION</h2>
                <button onClick={() => setSelected(null)} style={styles.closeBtn}><X /></button>
              </div>
              
              <div style={styles.modalBody}>
                <div style={styles.detailGrid}>
                  <DetailItem label="Authorized Entity" value={selected.userId?.name} />
                  <DetailItem label="Liquidated Vol." value={`$${Number(selected.amount).toFixed(2)}`} />
                  <DetailItem label="Protocol" value={selected.method} />
                  <DetailItem label="Initialization" value={new Date(selected.createdAt).toLocaleString()} />
                  
                  {selected.method === "External Ledger" ? (
                    <>
                      <DetailItem label="Network Node" value={selected.ledgerType} />
                      <DetailItem 
                        label="Destination Key" 
                        value={selected.destinationAddress} 
                        isCopyable={true} 
                        onCopy={() => copyToClipboard(selected.destinationAddress)} 
                      />
                    </>
                  ) : (
                    <>
                      <DetailItem label="Institution" value={selected.institutionName} />
                      <DetailItem 
                        label="Account ID" 
                        value={selected.accountIdentifier} 
                        isCopyable={true} 
                        onCopy={() => copyToClipboard(selected.accountIdentifier)} 
                      />
                      <DetailItem label="Routing Code" value={selected.routingCode} />
                    </>
                  )}
                </div>

                {(["Processing", "Pending", "Pending Verification"].includes(selected.status)) ? (
                  <div style={styles.actionBox}>
                    <label style={styles.txLabel}>EXTERNAL REFERENCE HASH / ID</label>
                    <input 
                      style={styles.txInput} 
                      placeholder="COMMIT TRANSACTION REFERENCE"
                      value={adminTxId}
                      onChange={(e) => setAdminTxId(e.target.value)}
                    />
                    
                    <div style={styles.modalActionRow}>
                      <button 
                        disabled={actionId === selected._id}
                        onClick={() => handleUpdateStatus(selected._id, "approve")} 
                        style={styles.approveBtn}
                      >
                        {actionId === selected._id ? "EXECUTING..." : "AUTHORIZE SETTLEMENT"}
                      </button>
                      <button 
                        disabled={actionId === selected._id}
                        onClick={() => handleUpdateStatus(selected._id, "reject")} 
                        style={styles.rejectBtn}
                      >
                        DECLINE
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.refDisplay}>
                    <div style={styles.detailLabel}>SETTLEMENT REFERENCE</div>
                    <div style={styles.refValue}>
                      {selected.clearanceReference || "NO_REF_LOGGED"}
                    </div>
                  </div>
                )}
              </div>
              <button onClick={() => setSelected(null)} style={styles.returnBtn}>RETURN TO INDEX</button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .spin { animation: spin 2s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: ${THEME.border}; border-radius: 10px; }
      `}</style>
    </div>
  );
}

const DetailItem = ({ label, value, isCopyable, onCopy }) => (
  <div style={styles.detailItem}>
    <div style={styles.detailLabel}>{label}</div>
    <div style={styles.detailValueRow}>
      <span style={styles.detailValue}>{value || "N/A"}</span>
      {isCopyable && value && (
        <button onClick={onCopy} style={styles.copyBtn}><Clipboard size={12} /> COPY</button>
      )}
    </div>
  </div>
);

const StatCard = ({ label, value, accent, icon }) => (
  <div style={{ ...styles.statCard, borderLeft: `3px solid ${accent}` }}>
    <div style={{ color: accent }}>{icon}</div>
    <div>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  </div>
);

const styles = {
  container: { display: "flex", backgroundColor: THEME.bg, height: "100vh", color: THEME.textMain },
  main: { flexGrow: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" },
  loadingBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 20, color: THEME.textMuted, fontSize: '12px', letterSpacing: '2px' },
  spinner: { background: THEME.surface, padding: 20, borderRadius: '50%', border: `1px solid ${THEME.border}` },
  
  topBar: { background: THEME.primary, padding: "20px 30px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${THEME.border}` },
  title: { margin: 0, fontSize: "16px", fontWeight: "900", letterSpacing: "2px" },
  subTitle: { margin: 0, fontSize: "10px", color: THEME.textMuted, letterSpacing: "1px" },
  liveIndicator: { display: "flex", alignItems: "center", gap: "10px", background: "rgba(0,0,0,0.3)", padding: "8px 16px", borderRadius: "6px" },
  pulseDot: { height: 8, width: 8, backgroundColor: THEME.success, borderRadius: "50%", boxShadow: `0 0 10px ${THEME.success}` },
  liveText: { fontSize: "10px", fontWeight: "900", letterSpacing: '1px' },

  scrollArea: { flexGrow: 1, overflowY: "auto" },
  content: { padding: "30px" },
  
  toast: (type) => ({ 
    position: "fixed", top: 100, right: 30, zIndex: 3000,
    background: type === "success" ? THEME.success : THEME.danger,
    color: "#fff", padding: "12px 24px", borderRadius: "8px", 
    display: 'flex', alignItems: 'center', gap: 10, fontWeight: "800", fontSize: "12px" 
  }),

  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px", marginBottom: "30px" },
  statCard: { background: THEME.surface, padding: "20px", borderRadius: "10px", display: 'flex', gap: 15, alignItems: 'center', border: `1px solid ${THEME.border}` },
  statLabel: { fontSize: "10px", color: THEME.textMuted, fontWeight: "800", textTransform: "uppercase" },
  statValue: { fontSize: "20px", fontWeight: "900" },

  filterBar: { display: "flex", flexWrap: "wrap", gap: "20px", marginBottom: "25px", alignItems: "center" },
  btnGroup: { display: "flex", gap: "10px" },
  filterBtn: (active) => ({ 
    padding: "10px 18px", borderRadius: "6px", border: active ? `1px solid ${THEME.accent}` : `1px solid ${THEME.border}`, 
    background: active ? "rgba(56, 189, 248, 0.1)" : THEME.surface, color: active ? THEME.accent : THEME.textMuted, 
    cursor: "pointer", fontSize: "10px", fontWeight: "900" 
  }),
  searchBox: { flex: 1, display: 'flex', alignItems: 'center', background: THEME.surface, padding: '0 15px', borderRadius: '8px', border: `1px solid ${THEME.border}`, maxWidth: '400px' },
  searchInput: { background: 'transparent', border: 'none', color: 'white', padding: '12px', outline: 'none', width: '100%', fontSize: '12px', fontWeight: '600' },

  tableCard: { background: THEME.surface, borderRadius: "12px", border: `1px solid ${THEME.border}`, overflow: 'hidden' },
  tableWrapper: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "18px", textAlign: "left", fontSize: "10px", color: THEME.textMuted, textTransform: "uppercase", fontWeight: "900", borderBottom: `1px solid ${THEME.border}` },
  td: { padding: "18px", borderBottom: `1px solid ${THEME.border}` },
  tr: { transition: "0.2s" },
  userName: { fontWeight: "800", fontSize: "13px" },
  userEmail: { fontSize: "11px", color: THEME.textMuted },
  balanceTag: { fontSize: "10px", color: THEME.accent, fontWeight: "900", marginTop: 4 },
  amount: { fontWeight: "900", color: THEME.textMain, fontSize: "15px" },
  methodName: { fontWeight: "700", fontSize: "12px" },
  subText: { fontSize: "10px", color: THEME.textMuted },
  timestamp: { fontSize: "11px", color: THEME.textMuted, lineHeight: '1.4' },
  processBtn: { background: "rgba(56, 189, 248, 0.1)", color: THEME.accent, border: `1px solid ${THEME.accent}`, padding: "8px 16px", borderRadius: "6px", cursor: "pointer", fontWeight: "900", fontSize: "10px" },
  empty: { padding: "50px", textAlign: "center", color: THEME.textMuted, fontWeight: "bold", fontSize: '12px' },

  pagination: { display: "flex", padding: "20px", justifyContent: "space-between", alignItems: "center", background: THEME.primary },
  pBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: "8px 16px", borderRadius: "6px", border: `1px solid ${THEME.border}`, background: THEME.surface, color: THEME.textMain, cursor: "pointer", fontWeight: "900", fontSize: "10px" },
  pageInfo: { fontSize: "11px", fontWeight: "800", color: THEME.textMuted },

  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0, 0, 0, 0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4000, backdropFilter: "blur(8px)", padding: "20px" },
  modalContent: { background: THEME.primary, padding: "30px", borderRadius: "15px", width: "100%", maxWidth: "600px", border: `1px solid ${THEME.border}` },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px", borderBottom: `1px solid ${THEME.border}`, paddingBottom: "15px" },
  modalTitle: { margin: 0, fontSize: "15px", fontWeight: "900", display: 'flex', alignItems: 'center' },
  closeBtn: { background: 'none', border: 'none', color: THEME.textMuted, cursor: 'pointer' },
  modalBody: { display: "flex", flexDirection: "column", gap: "25px" },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" },
  detailItem: { display: "flex", flexDirection: "column", gap: "6px" },
  detailLabel: { fontSize: "9px", color: THEME.textMuted, fontWeight: "900", letterSpacing: '1px' },
  detailValueRow: { display: "flex", alignItems: "center", gap: "10px" },
  detailValue: { fontWeight: "700", fontSize: "13px" },
  copyBtn: { background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.accent, fontSize: "9px", padding: "4px 8px", borderRadius: "4px", cursor: "pointer", fontWeight: "900", display: 'flex', alignItems: 'center', gap: 5 },
  
  actionBox: { padding: "20px", background: THEME.surface, borderRadius: "10px", border: `1px solid ${THEME.border}` },
  txLabel: { display: "block", marginBottom: "10px", fontSize: "9px", fontWeight: "900", color: THEME.textMuted },
  txInput: { width: "100%", background: THEME.bg, border: `1px solid ${THEME.border}`, padding: "12px", borderRadius: "8px", color: "white", outline: "none", fontSize: "13px", marginBottom: "20px" },
  modalActionRow: { display: "flex", gap: "15px" },
  approveBtn: { background: THEME.success, color: "#fff", border: "none", padding: "14px", borderRadius: "8px", cursor: "pointer", flex: 2, fontWeight: "900", fontSize: "12px" },
  rejectBtn: { background: "transparent", color: THEME.danger, border: `1px solid ${THEME.danger}`, padding: "14px", borderRadius: "8px", cursor: "pointer", flex: 1, fontWeight: "900", fontSize: "12px" },
  refDisplay: { textAlign: "center", padding: 20, background: THEME.surface, borderRadius: 10 },
  refValue: { fontWeight: "900", color: THEME.success, marginTop: 10, fontSize: '14px', letterSpacing: '1px' },
  returnBtn: { width: "100%", marginTop: "25px", padding: "14px", border: `1px solid ${THEME.border}`, background: "transparent", borderRadius: "8px", cursor: "pointer", fontWeight: "900", color: THEME.textMuted, fontSize: "11px" },

  statusBadge: (status) => {
    const isGood = ["Completed", "Settlement Finalized"].includes(status);
    const isWarn = ["Pending", "Processing", "Pending Verification"].includes(status);
    return {
      background: isGood ? "rgba(16, 185, 129, 0.1)" : isWarn ? "rgba(245, 158, 11, 0.1)" : "rgba(244, 63, 94, 0.1)",
      color: isGood ? THEME.success : isWarn ? THEME.warning : THEME.danger,
      padding: "5px 12px", borderRadius: "6px", fontSize: "9px", fontWeight: "900", display: "inline-block"
    };
  }
};